import { HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ITenant, Tenant } from '@shared/models/tenant';
import { AppUser } from '@core/services/user.service';
import { U2bAdminGenericApi } from '@modules/admin/state/u2b-admin-generic.api';
import {
    AdminTenantModulePermission,
    AdminTenantModulePermissionUpdate,
} from '@modules/admin/tenants/models/admin-tenant-module-permission.model';
import {
    AdminTenantOrderManagementRollout,
    AdminTenantOrderManagementRolloutUpdate,
} from '@modules/admin/tenants/models/admin-tenant-order-management-rollout.model';
import {
    TenantTseIntegrationDto,
    TenantTseIntegrationTestResultDto,
    TenantTseIntegrationUpsertDto,
} from '@shared/models/tenant-tse-integration.model';
import {
    AdminTenantSupportWhitelistEntry,
    AdminTenantSupportWhitelistUserSearchResult,
} from '@modules/admin/tenants/models/admin-tenant-support-whitelist.model';
import { parseHttpResponseItem } from '@shared/functions/parse-http-response';
import { AdminTenantPleskBackupUpdateResult } from '@modules/admin/tenants/models/admin-tenant-plesk-backup.model';
import {
    AdminMysqlInfrastructureDto,
    AdminTenantsListResponseDto,
} from '@modules/admin/tenants/models/admin-tenant-infrastructure.model';
import { AdminTenantSubscriptionSchedulerUpdateResult } from '@modules/admin/tenants/models/admin-tenant-subscription-scheduler.model';

export interface AdminTenantsListLoadResult {
    tenants: Tenant[];
    infrastructure: AdminMysqlInfrastructureDto;
}

export class U2bAdminTenantsApi extends U2bAdminGenericApi<Tenant, ITenant> {

    loadAllWithInfrastructure(): Observable<AdminTenantsListLoadResult> {
        return super.get<AdminTenantsListResponseDto | ITenant[]>(this.resourceConfig.endpointPath)
            .pipe(map((response) => {
                if (Array.isArray(response)) {
                    return {
                        tenants: response.map((raw) => new this.resourceConfig.entityClass(raw)),
                        infrastructure: {
                            mysqlEventSchedulerEnabled: null,
                            checkedAt: new Date().toISOString(),
                        },
                    };
                }

                return {
                    tenants: (response?.tenants || []).map(
                        (raw) => new this.resourceConfig.entityClass(raw as ITenant),
                    ),
                    infrastructure: response?.infrastructure || {
                        mysqlEventSchedulerEnabled: null,
                        checkedAt: new Date().toISOString(),
                    },
                };
            }));
    }

    updateMysqlEventScheduler(enabled: boolean): Observable<AdminMysqlInfrastructureDto> {
        return super.put<AdminMysqlInfrastructureDto>(
            `${this.resourceConfig.endpointPath}/infrastructure/mysql-event-scheduler`,
            { enabled },
        );
    }

    updateSubscriptionScheduler(
        tenant: Tenant,
        enabled: boolean,
    ): Observable<AdminTenantSubscriptionSchedulerUpdateResult> {
        return super.put<AdminTenantSubscriptionSchedulerUpdateResult>(
            `${this.resourceConfig.endpointPath}/${tenant.id}/subscription-scheduler`,
            { enabled },
        );
    }

    // AVV
    getAvv(tenant: Tenant, params: HttpParams = new HttpParams()): Observable<any> {
        return super.get(`${super.resourceConfig.endpointPath}/avv`, params);
    }

    getAvvPdf(tenant: Tenant, params: HttpParams = new HttpParams()): Observable<any> {
        return super.getBlob(`${this.resourceConfig.endpointPath}/avv/pdf`, params);
    }

    // Users
    addUser(tenant: Tenant, body: Partial<AppUser> = {}): Observable<AppUser> {
        return super.post<AppUser>(`${this.resourceConfig.endpointPath}/users`, body);
    }

    removeUser(tenant: Tenant, user: AppUser): Observable<unknown> {
        return super.delete<unknown>(`${this.resourceConfig.endpointPath}/users/${user.id}`);
    }

    getModulePermissions(tenant: Tenant): Observable<AdminTenantModulePermission[]> {
        return super.get<AdminTenantModulePermission[]>(
            `${this.resourceConfig.endpointPath}/${tenant.id}/bcm-permissions`,
        );
    }

    getOrderManagementRollouts(tenant: Tenant): Observable<AdminTenantOrderManagementRollout[]> {
        return super.get<AdminTenantOrderManagementRollout[]>(
            `${this.resourceConfig.endpointPath}/${tenant.id}/order-management-rollout`,
        );
    }

    updateWithModulePermissions(
        tenant: Tenant,
        data: Partial<Tenant> & { bcmModulePermissions?: AdminTenantModulePermissionUpdate[] },
    ): Observable<Tenant> {
        return super.put<ITenant>(`${this.resourceConfig.endpointPath}/${tenant.id}`, data)
            .pipe(parseHttpResponseItem<ITenant, Tenant>(this.resourceConfig.entityClass));
    }

    updateOrderManagementRollouts(
        tenant: Tenant,
        flags: AdminTenantOrderManagementRolloutUpdate[],
    ): Observable<AdminTenantOrderManagementRollout[]> {
        return super.put<AdminTenantOrderManagementRollout[]>(
            `${this.resourceConfig.endpointPath}/${tenant.id}/order-management-rollout`,
            { flags },
        );
    }

    getTenantTseIntegration(tenant: Tenant): Observable<TenantTseIntegrationDto> {
        return super.get<TenantTseIntegrationDto>(
            `${this.resourceConfig.endpointPath}/${tenant.id}/tse-integration`,
        );
    }

    saveTenantTseIntegration(
        tenant: Tenant,
        payload: TenantTseIntegrationUpsertDto,
    ): Observable<TenantTseIntegrationDto> {
        return super.put<TenantTseIntegrationDto>(
            `${this.resourceConfig.endpointPath}/${tenant.id}/tse-integration`,
            payload,
        );
    }

    testTenantTseIntegration(
        tenant: Tenant,
        payload: TenantTseIntegrationUpsertDto,
    ): Observable<TenantTseIntegrationTestResultDto> {
        return super.post<TenantTseIntegrationTestResultDto>(
            `${this.resourceConfig.endpointPath}/${tenant.id}/tse-integration/test`,
            payload,
        );
    }

    updatePleskBackup(tenant: Tenant, enabled: boolean): Observable<AdminTenantPleskBackupUpdateResult> {
        return super.put<AdminTenantPleskBackupUpdateResult>(
            `${this.resourceConfig.endpointPath}/${tenant.id}/plesk-backup`,
            { enabled },
        );
    }

    getSupportWhitelist(tenant: Tenant): Observable<AdminTenantSupportWhitelistEntry[]> {
        return super.get<AdminTenantSupportWhitelistEntry[]>(
            `${this.resourceConfig.endpointPath}/${tenant.id}/support-whitelist`,
        );
    }

    searchSupportWhitelistUsers(
        tenant: Tenant,
        query: string,
    ): Observable<AdminTenantSupportWhitelistUserSearchResult[]> {
        const params = new HttpParams().set('q', query);
        return super.get<AdminTenantSupportWhitelistUserSearchResult[]>(
            `${this.resourceConfig.endpointPath}/${tenant.id}/support-whitelist/search-users`,
            params,
        );
    }

    addSupportWhitelistUser(tenant: Tenant, userId: string): Observable<AdminTenantSupportWhitelistEntry> {
        return super.post<AdminTenantSupportWhitelistEntry>(
            `${this.resourceConfig.endpointPath}/${tenant.id}/support-whitelist`,
            { userId },
        );
    }

    patchSupportWhitelistEntry(
        tenant: Tenant,
        entryId: number,
        enabled: boolean,
    ): Observable<AdminTenantSupportWhitelistEntry> {
        return super.put<AdminTenantSupportWhitelistEntry>(
            `${this.resourceConfig.endpointPath}/${tenant.id}/support-whitelist/${entryId}`,
            { enabled },
        );
    }

    deleteSupportWhitelistEntry(tenant: Tenant, entryId: number): Observable<{ ok: boolean }> {
        return super.delete<{ ok: boolean }>(
            `${this.resourceConfig.endpointPath}/${tenant.id}/support-whitelist/${entryId}`,
        );
    }
}

